import L from 'leaflet'
import { clone } from 'lodash-unified'

import dayjs from 'dayjs'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { result } from '@map-sdk/sdk/plugins/Trackplayer/data'
import { trackPlayServiceImpl } from '@map-sdk/sdk/plugins/Trackplayer/TrackPlayServiceImpl'
import { trackSymbol } from '@map-sdk/sdk/plugins/Trackplayer/TrackSymbolImpl'
import type { LatLngExpression } from 'leaflet'
import type { CanvasShip } from '@map-sdk/sdk/model/CanvasShip'
/**
 * 轨迹
 */
export class Track {
  from: string | number = 0
  lng: string | number = 0
  lat: string | number = 0
  utc: string | number = 0
  mmsi: string | number = 0
  sog: string | number = 0
  hdg: string | number = 0
  cog: string | number = 0
  draught?: string
  dest: undefined
  eta: undefined
  serverTime: undefined
  shipName?: string
  MMSI?: string
  rot?: string
  navistatus: any
}
interface EmissionDataItem {
  [key: string]: any
  time: number
  co2: number
}

interface EmissionConfig {
  datas: EmissionDataItem[]
  fieldsMap: {
    time: string
    co2: string
  }
  scalesMap: {
    co2: number
  }
  colorsMap: {
    co2: any[] // Assuming this is an array of color objects or values
  }
}

interface TrackEmission {
  utc: number
  emissionCo2: number | null
  emissionCo2Color: string | null
  emissionCo2Count?: number
}
export interface TrackItem {
  trackId: string
  data: any[]
  trackLang: number | string
  symbol: any
  options: any
  ship: any
}

interface TrackServiceOptions {
  from180: any
  minStopTime: number
  minSlowTime: number
  minSuspectStopSpeed: number
  maxStopSpeed: number
  maxSlowSpeed: number
  playSpeed: number
  beginTime: number
  playCallBack?: () => void
  playGPSCallBack?: () => void
  isVisiableWithNoSignal: boolean
  showPlayObjLable: boolean
}

export interface TrackPolyLineOptions {
  maxStopSpeed: number
  lableMap: Record<string, any>
  showTimeType: string[]
  notDiluteZoom: number[]
  lineColor: string // 轨迹线颜色
  lineWeight: number // 线粗细，默认：1
  circleOverColor: string // 拐点，鼠标滑过时的颜色
  dash: boolean // 是否为虚线，true：虚线，false：实线，默认：false
  dashArray: number[] // 虚线参数，默认：[5,5]
  startShow: boolean // 是否显示“起点”图标，默认值：true
  endShow: boolean // 是否显示“终点”图标，默认值：true
  directionShow: boolean // 轨迹方向三角是否展示，默认值：false
  isEnablePlayer: boolean // 是否需要播放轨迹，默认值：false
  isShowLable: boolean // 是否显示lable，默认值：true
  lableFields: string[] // 轨迹点信息显示，默认值：["name", "mmsi", "sog", "cog", "utc", "kn"]
  shipColor?: string
  shipLineColor?: string
  from180?: number

  tipHideTimeOut: number // 轨迹/航线，tip 超时关闭时间设置，默认：8000
  startCircleType: number // 起始圆圈样式类型，0：不显示，1:同心圆，2：普通圆圈，与拐点相同，默认：1
  endCircleType: number // 结束圆圈样式类型，0：不显示，1:同心圆，2：普通圆圈，与拐点相同，默认：1
  startCircleColor: string // 起始圆圈填充色，默认：'#06c84a'
  endCircleColor: string // 结束圆圈填充色，默认：'#f70006'
  isAppend: boolean // 是否追加
}

export class TrackServiceImpl {
  private _map: any
  private options: TrackServiceOptions
  private items: { [key: string]: TrackItem }
  private trackPlayService: any
  mmCount = 0

  constructor(map: any, options?: Partial<TrackServiceOptions>) {
    this.options = {
      minStopTime: 300,
      minSlowTime: 300,
      minSuspectStopSpeed: 3,
      maxStopSpeed: 0.5,
      maxSlowSpeed: 5,
      from180: true,
      playSpeed: 1,
      beginTime: 0,
      isVisiableWithNoSignal: true,
      showPlayObjLable: true,
    }

    this.items = {}
    this._map = map
    this.options = { ...this.options, ...options }
    this.trackPlayService = trackPlayServiceImpl(this._map, this.options)
    this.trackPlayService.maxSupplementPointInterval = 0
  }

  /**
   * 增加轨迹
   * @params trackId String	轨迹ID
   * @params tracks Array	Track对象集合
   * @params options Object	设置信息
   * @params ship CanvasShip	船舶静态数据{name:船名,mmsi:MMSI,from:数据来源,navistatus:航行状态,draught:吃水}
   */
  add(
    trackId: string,
    trackData: any[],
    options: Partial<TrackPolyLineOptions>,
    shipInfo: Partial<CanvasShip>
  ) {
    // 处理from180参数
    let from180 = this.options.from180
    if (!options || CommUtils.isEmpty(options.from180)) {
      from180 = options.from180
    }

    // 如果已经存在该轨迹ID，则先移除
    if (this.has(trackId) && !options.isAppend) {
      this.remove(trackId)
    }

    // 转换经纬度数据，如果from180标志为真
    if (from180 === 1) {
      trackData = CanvasShipUtils.objFrom180(trackData, '', 0)
    }

    // 自动设置视图边界
    trackData = this.autoSetViewBounds(trackData)

    // 合并选项参数
    const finalOptions = Object.assign(
      {},
      {
        addAndShowPlayer: true,
      },
      options
    )

    // 如果没有提供shipInfo，则初始化为空对象
    if (!shipInfo) {
      shipInfo = {}
    }

    // 计算轨迹语言
    const trackLang = this.calculationTracklang(trackData)

    // 添加到轨迹列表中
    if (!this.items[trackId]) {
      this.items[trackId] = {
        trackId,
        data: trackData,
        trackLang,
        symbol: null,
        options: finalOptions,
        ship: shipInfo,
      }
    } else {
      this.items[trackId].data = [...this.items[trackId].data, ...trackData]
    }

    // 返回添加的轨迹项
    return this.items[trackId]
  }

  autoSetViewBounds(trackData: any[]) {
    // 如果轨迹列表为空，则直接返回原始数据
    if (!this.items) {
      return trackData
    }

    // 获取现有轨迹ID列表
    const trackIds = Object.keys(this.items)

    // 如果轨迹ID列表为空，则直接返回原始数据
    if (!trackIds || trackIds.length === 0) {
      return trackData
    }

    // 获取当前轨迹的中心点
    const currentCenter = this.getCenterByTracks(trackData)

    // 遍历现有轨迹，寻找中心点
    let referenceCenter: L.LatLng = new L.LatLng(0, 0)
    trackIds.forEach((trackId) => {
      const item = this.items[trackId]
      referenceCenter = this.getCenterByTracks(item.data)
    })

    // 如果没有找到参考中心点，则直接返回原始数据
    if (!referenceCenter) {
      return trackData
    }

    // 计算当前轨迹中心点与参考中心点的经度差
    const lngDiff = Math.abs(referenceCenter.lng - currentCenter.lng)
    const lngDiffPlus180 = Math.abs(
      referenceCenter.lng - (currentCenter.lng + 180)
    )
    const lngDiffMinus180 = Math.abs(
      referenceCenter.lng - (currentCenter.lng - 180)
    )
    const minLngDiff = Math.min(lngDiff, lngDiffPlus180, lngDiffMinus180)
    let offset = 0

    // 根据最小经度差确定偏移量
    switch (minLngDiff) {
      case lngDiff:
        offset = 0
        break
      case lngDiffPlus180:
        offset = 180
        break
      case lngDiffMinus180:
        offset = -180
    }

    // 如果偏移量为0，则直接返回原始数据
    if (offset === 0) {
      return trackData
    }

    // 应用偏移量到所有点
    const offsetData: any[] = []
    trackData.forEach((point) => {
      point.lng += offset
      offsetData.push(point)
    })

    // 返回调整后的轨迹数据
    return offsetData
  }

  getCenterByTracks(trackPoints: any[]) {
    // 使用Leaflet的polyline方法根据轨迹点创建一个虚拟的线
    // 然后获取这个线的边界（Bounds）并计算中心点
    const bounds = L.polyline(trackPoints).getBounds()
    return bounds.getCenter()
  }
  formateTrack(tracks: any[], shipname: string, mmsi: string) {
    const formateTracks = []
    for (const track of tracks) {
      const point = new Track()
      point.shipName = shipname
      point.MMSI = mmsi
      point.from = track.from
      point.utc = track.utc
      point.hdg = (track.hdg / 100).toFixed(1)
      point.sog = (track.sog / 514).toFixed(1)
      point.cog = (track.cog / 100).toFixed(1)
      point.rot = (track.rot / 100).toFixed(1)
      point.lng = (track.lon || track.lng) / 1000000
      point.lat = track.lat / 1000000
      point.navistatus = track.navistatus
      formateTracks.push(point)
    }
    return formateTracks
  }

  /**
   * 增加轨迹并展示
   * @params trackId	String	轨迹ID
   * @params tracks	Array	Track对象集合
   * @params options	TrackPolyLineOptions	设置信息
   * @params ship	CanvasShip	船舶静态数据{name:船名,mmsi:MMSI,from:数据来源,navistatus:航行状态,draught:吃水}
   * @params autoViewzoom	boolea	是否自适应展示,默认:true
   * @return trackItem
   */
  addAndShow(
    trackId: string,
    tracks: any[],
    options: Partial<TrackPolyLineOptions>,
    ship: any,
    autoViewzoom = true
  ): TrackItem {
    tracks = this.formateTrack(
      tracks.length ? tracks : result,
      ship.name || '测试数据',
      ship.mmis || '123123'
    )
    this.add(trackId, tracks, options, ship)
    return this.show(trackId, autoViewzoom)
  }
  /**
   * 添加轨迹（根据参数自动获取轨迹数据）
   * @params mmsi	string	mmsi
   * @params btime	number	起始时间戳，秒
   * @params etime	number	结束时间戳，秒
   * @params options	Object	设置信息
   * @params callBack	function	回调方法
   * @params tp	number	轨迹数据类型，0：普通,1：抽稀,2：完整，默认：0
   * @params vy	number	航程，0：无,1：有，默认：0
   * @params autoViewzoom	boolea	是否自适应展示,默认:true
   */
  addAndShowByUrl(
    mmsi: string,
    btime: string,
    etime: string,
    options: Partial<TrackPolyLineOptions>,
    callBack: (arg0: any) => void,
    tp: string | number | string[] | null | undefined,
    vy: string | number | string[] | null | undefined,
    autoViewzoom: boolean
  ) {
    let trackItem: TrackItem | undefined
    const key = `${mmsi}_${btime}_${etime}`
    try {
      if (
        (CommUtils.isEmpty(tp) && (tp = 0),
        CommUtils.isEmpty(vy) && (vy = 0),
        this.has(key))
      )
        trackItem = this.show(key)
      else {
        // mmsi, btime, etime, false, tp, vy
        const trackInfo = this.getTrackInfoByUrl()
        if (!trackInfo || 0 == trackInfo.length) return null
        let shipByMmsi = {} as any
        !this._map.shipsService &&
          this._map.shipsService(this._map, {
            enableAreaShip: false,
            enableFleetShip: false,
          })
        this._map.shipsService &&
          (shipByMmsi = this._map.shipsService.getShipByMmsi(mmsi, true))
        !shipByMmsi && (shipByMmsi = {})
        shipByMmsi.mmsi = mmsi
        if (options) {
          if (options.shipColor) {
            shipByMmsi.color = options.shipColor
          }
          options.shipLineColor &&
            (shipByMmsi.lineColor = options.shipLineColor)
        }
        trackItem = this.addAndShow(
          key,
          trackInfo,
          options,
          shipByMmsi,
          autoViewzoom
        )
      }
    } catch (e) {
      console.error(e)
    } finally {
      callBack &&
        setTimeout(() => {
          callBack(trackItem)
        }, 1),
        CanvasShipUtils.closeLoadingPopup()
    }
    return trackItem
  }

  appendTracks(trackId: string, tracks: any[], aiDetectionList?: any[]) {
    const item = this.items[trackId]
    if (!item) {
      console.error(
        '未找到航线信息请先调用addAndShow进行展示首段信息后再调用该方法'
      )
      return
    }
    tracks = this.formateTrack(tracks, item.ship.name, item.ship.MMSI)
    const lastPoint = item.data[item.data.length - 1]
    lastPoint.isShowMarker = false
    tracks = [lastPoint, ...tracks]
    // tracks = [item.data[item.data.length - 1], ...tracks]
    const mergeOptions = { ...item.options, isAppend: true }
    this.add(trackId, tracks, mergeOptions, item.ship)
    this._analysisTrack(trackId)
    this._analysisEmission(tracks, this.getEmissionOptions(mergeOptions))
    this.show(trackId, false, true)
    item.symbol.appendLine(tracks, aiDetectionList)
  }
  appendFinish(trackId: string) {
    const item = this.items[trackId]
    item.symbol.resetCache()
  }
  getEmissionOptions(options: any) {
    // 处理排放数据选项
    const emissionOptions = options.emission
      ? Object.assign(
          true,
          {
            overlayMinStopTime: 1,
            weight: 20,
            colorsMap: {
              default: 'rgba(0,0,0,0)',
              co2: [
                { value: 5, color: 'rgba(217,245,214,0.6)' },
                { value: 10, color: 'rgba(98,210,86,0.8)' },
                { value: 20, color: 'rgba(255,246,122,0.8)' },
                { value: 30, color: 'rgba(255,233,40,0.8)' },
                { value: 50, color: 'rgba(251,191,188,0.8)' },
                { value: 3600, color: 'rgba(247,105,100,0.8)' },
              ],
            },
            scalesMap: {
              co2: 1000,
            },
            fieldsMap: {
              time: 'datekey_rt',
              co2: 'vessel_co2_day_atom_v2',
            },
            datas: [],
          },
          options.emission
        )
      : null
    options.emission = emissionOptions
    return emissionOptions
  }

  /**
   * 显示指定轨迹
   * @params trackId	String	轨迹ID
   * @params autoViewzoom	boolea	是否自适应展示,默认:true
   */
  show(trackId: string, autoViewzoom = true, append = false) {
    // 检查是否有该轨迹
    if (this.has(trackId)) {
      const item = this.items[trackId]

      // 如果轨迹没有符号化，进行符号化处理
      const options = item.options
      if (!item.symbol) {
        const trackLang = item.trackLang

        // 处理排放数据选项
        const emissionOptions = this.getEmissionOptions(options)

        // 分析轨迹和排放数据
        this._analysisTrack(trackId)
        this._analysisEmission(item.data, emissionOptions)

        // 创建轨迹符号
        const trackOptions = L.extend(
          this.options,
          {
            trackid: trackId,
            trackLangCount: trackLang,
          },
          options
        )
        item.symbol = trackSymbol(this._map, item.data, item.ship, trackOptions)
      }

      // 定位缩放到合适位置
      autoViewzoom && this._viewAllTrackInMap(trackId)

      // 设置轨迹为新状态并显示
      if (!append) {
        item.symbol.trackNew = true
        item.symbol.show()
      }
      // 更新船舶服务的z-index
      if (this._map.shipsService) {
        this._map.shipsService.setZIndex()
      }

      // 检查是否启用播放器并根据需要添加和显示
      let isEnablePlayer = options.isEnablePlayer
      isEnablePlayer = isEnablePlayer === null ? false : !!isEnablePlayer
      const addAndShowPlayer = options.addAndShowPlayer

      if (isEnablePlayer && addAndShowPlayer !== 1) {
        this.trackPlayService.addPlayTrack(trackId, false, options.isAutoPlay)
      }
    }

    // 刷新地图视图
    // this._map.refreshView()

    // 返回当前项
    return this.items[trackId]
  }

  calculationTracklang(data: string | any[]) {
    let md = 0
    const dataLength = data.length
    for (let i = 0; i < dataLength; i++) {
      data[i].index = i
      CommUtils.isEmpty(data[i].lng) && (data[i].lng = data[i].lon)
      i + 1 < dataLength
        ? ((data[i].md = CanvasShipUtils.getDistance(
            data[i],
            data[i + 1],
            'nm'
          )),
          (data[i].cog = CanvasShipUtils.getAngleByLatLngToBigCircle(
            data[i],
            data[i + 1]
          )))
        : ((data[i].md = 0), (data[i].cog = 0))
      md += data[i].md
      data[i].kn = md - data[i].md
    }

    let countMd = 0
    for (let i = dataLength - 1; i >= 0; i--)
      i < dataLength - 1 && (countMd += data[i].md), (data[i].ed = countMd)
    return md.toFixed(2)
  }
  _analysisTrack(trackIndex: string) {
    const minStopTime = this.options.minStopTime // 最小停靠时间
    const minSlowTime = this.options.minSlowTime // 最小慢行时间
    const maxStopSpeed = this.options.maxStopSpeed // 最大停止速度
    const maxSlowSpeed = this.options.maxSlowSpeed // 最大慢行速度
    const minSuspectStopSpeed = this.options.minSuspectStopSpeed // 疑似停止的最小速度
    const tracksData = this.items[trackIndex].data // 轨迹数据
    const trackAnalysis = {
      startTime: null,
      endTime: null,
      time: 0,
      _time: '',
      isShow: false,
      tracks: [],
      tracksHash: {},
      type: null,
    }
    let currentTrack, nextTrack, prevTrack
    let analysisInfo: any = null,
      suspectStopInfo = null

    for (let i = 0; i < tracksData.length; i++) {
      currentTrack = tracksData[i]
      nextTrack = tracksData[i + 1]
      prevTrack = tracksData[i - 1]

      const speed = currentTrack.sog
      // 判断航速状态
      const type =
        speed <= maxStopSpeed ? 'stop' : speed < maxSlowSpeed ? 'slow' : 'none'
      if (analysisInfo && analysisInfo.type === type && type !== 'none') {
        analysisInfo.endTrackIndex = i
        analysisInfo.count =
          analysisInfo.endTrackIndex - analysisInfo.startTrackIndex + 1
        analysisInfo.endTime = currentTrack.utc
        analysisInfo.time = analysisInfo.endTime - analysisInfo.startTime
        // console.log(CommUtils.dataFormat(1717455009000),analysisInfo.time*1000);
        // 创建两个时间戳对象
        const time1 = dayjs(analysisInfo.endTime)
        const time2 = dayjs(analysisInfo.startTime)

        // 计算时间差
        const duration = time1.diff(time2)

        // 获取时间差相差的天数、小时数、分钟数和秒数
        const days = duration / 1000 / 60 / 60 / 24
        const hours = (duration / 1000 / 60 / 60) % 24
        const minutes = (duration / 1000 / 60) % 60
        const seconds = (duration / 1000) % 60
        analysisInfo._time = {
          days,
          hours,
          minutes,
          seconds,
        } //CommUtils.dataFormat(analysisInfo.time, 'HH:mm:ss')
      } else if (
        (analysisInfo &&
          analysisInfo.type === 'slow' &&
          analysisInfo.time >= minSlowTime) ||
        (analysisInfo &&
          analysisInfo.type === 'stop' &&
          analysisInfo.time >= minStopTime)
      ) {
        this._reSetTrackByAnalysisInfo(tracksData, analysisInfo)
        analysisInfo = null
      } else if (type !== 'none') {
        analysisInfo = clone(trackAnalysis)
        analysisInfo.trackId = trackIndex
        analysisInfo.type = type
        analysisInfo.startTrackIndex = i
        analysisInfo.startTime = currentTrack.utc
      }

      if (
        nextTrack &&
        prevTrack &&
        speed >= minSuspectStopSpeed &&
        nextTrack.sog >= minSuspectStopSpeed
      ) {
        const avgSpeed = (speed + nextTrack.sog) / 2
        const timeDiff = nextTrack.utc - currentTrack.utc
        const timeInSeconds = timeDiff / 1000
        const distanceCovered = currentTrack.md / timeInSeconds

        if (timeInSeconds >= 0.5 && 0.6 * avgSpeed > distanceCovered) {
          const stopMightSeconds = timeInSeconds - currentTrack.md / avgSpeed
          suspectStopInfo = {
            type: 'suspectStop',
            startTrackIndex: i,
            endTrackIndex: i + 1,
            md: currentTrack.md,
            time: timeInSeconds.toFixed(2),
            _time: CommUtils.dataFormat(timeDiff),
            sogAvg: distanceCovered.toFixed(1),
            sogMight: avgSpeed.toFixed(1),
            stopMight: stopMightSeconds.toFixed(2),
            _stopMight: CommUtils.dataFormat(1000 * stopMightSeconds),
          }
          currentTrack.analysisInfo = clone(suspectStopInfo)
        }
      }
    }

    if (
      (analysisInfo &&
        analysisInfo.type === 'slow' &&
        analysisInfo.time >= minSlowTime) ||
      (analysisInfo &&
        analysisInfo.type === 'stop' &&
        analysisInfo.time >= minStopTime)
    ) {
      this._reSetTrackByAnalysisInfo(tracksData, analysisInfo)
    }
  }

  _reSetTrackByAnalysisInfo(
    tracks: any[],
    analysisInfo: {
      startTime?: null
      endTime?: null
      time?: number
      _time?: string
      isShow?: boolean
      tracks?: never[]
      tracksHash?: Record<string, any>
      type?: null
      startTrackIndex?: any
      endTrackIndex?: any
    }
  ) {
    // 检查分析信息和轨迹数据是否有效且轨迹数据不为空
    if (analysisInfo && tracks && tracks.length > 0) {
      // 遍历分析信息指示的轨迹索引范围
      for (
        let startIndex = analysisInfo.startTrackIndex;
        startIndex <= analysisInfo.endTrackIndex;
        startIndex++
      ) {
        tracks[startIndex].analysisInfo = { ...analysisInfo, index: startIndex }

        // 如果这是第一次处理，则初始化mmCount
        if (this.mmCount === null || typeof this.mmCount === 'undefined') {
          this.mmCount = 0
        }
        // 增加处理计数
        this.mmCount++
      }
    }
  }
  _analysisEmission(
    tracks: TrackEmission[],
    emissionConfig?: EmissionConfig
  ): TrackEmission[] {
    if (emissionConfig) {
      const emissionData: {
        [key: number]: {
          time: number
          _time: number
          co2h: number
          co2s: number
        }
      } = {}
      emissionConfig.datas.forEach((dataItem) => {
        const time = dataItem[emissionConfig.fieldsMap.time]
        const dailyCo2 =
          dataItem[emissionConfig.fieldsMap.co2] /
          emissionConfig.scalesMap.co2 /
          24
        const hourlyCo2 = dailyCo2 / 3600
        emissionData[time] = {
          time,
          _time: new Date(time).getTime() / 1000,
          co2h: dailyCo2,
          co2s: hourlyCo2,
        }
      })

      let previousTimestamp = null
      let accumulatedEmission = 0
      for (const track of tracks) {
        const dateStr = CommUtils.dataFormat(
          new Date(1000 * track.utc),
          'YYYY-MM-DD'
        )
        const emissionForDay = emissionData[dateStr]

        if (emissionForDay) {
          track.emissionCo2 = emissionForDay.co2h
          track.emissionCo2Color = this._getTrackEmissionColor(
            track.emissionCo2,
            emissionConfig.colorsMap.co2
          )
          if (previousTimestamp !== null) {
            accumulatedEmission +=
              (track.utc - previousTimestamp) * emissionForDay.co2s
          }
        } else {
          track.emissionCo2 = null
          track.emissionCo2Color = null
        }
        track.emissionCo2Count = accumulatedEmission
        previousTimestamp = track.utc
      }
    } else {
      tracks.forEach((track) => {
        Object.assign(track, {
          emissionCo2: null,
          emissionCo2Color: null,
          emissionCo2Count: null,
        })
      })
    }

    return tracks
  }
  _getTrackEmissionColor(
    emissionValue: number | null,
    segments: string | any[]
  ) {
    if (!emissionValue) return
    for (let index = 0; index < segments.length; index++) {
      if (
        index === segments.length - 1 ||
        emissionValue < segments[index].value
      ) {
        return segments[index].color
      }
    }
    return null
  }
  _getTrackEmissionCount(
    startTimeInSeconds: number,
    endTimeInSeconds: number,
    co2EmissionData: { [x: string]: { co2s: any } }
  ) {
    const startDate = new Date(startTimeInSeconds * 1000)
    const endDate = new Date(endTimeInSeconds * 1000)
    const currentDateStr = CommUtils.dataFormat(startDate, 'yyyy-MM-dd')
    let totalEmission = 0

    if (currentDateStr === CommUtils.dataFormat(endDate, 'yyyy-MM-dd')) {
      totalEmission =
        (endTimeInSeconds - startTimeInSeconds) *
        ((co2EmissionData[currentDateStr] &&
          co2EmissionData[currentDateStr].co2s) ||
          0)
    } else {
      let currentTimeInSeconds = startTimeInSeconds,
        nextDayStartTimeInSeconds =
          new Date(currentDateStr).getTime() / 1000 + 50400,
        nextDayStr = CommUtils.dataFormat(
          nextDayStartTimeInSeconds * 1000,
          'yyyy-MM-dd'
        ),
        lastDateKey = nextDayStr

      if (currentTimeInSeconds < endTimeInSeconds) {
        totalEmission +=
          (nextDayStartTimeInSeconds - currentTimeInSeconds) *
          ((co2EmissionData[nextDayStr] && co2EmissionData[nextDayStr].co2s) ||
            0)

        currentTimeInSeconds = nextDayStartTimeInSeconds
        lastDateKey = nextDayStr
        nextDayStartTimeInSeconds += 50400
        nextDayStr = CommUtils.dataFormat(
          nextDayStartTimeInSeconds * 1000,
          'yyyy-MM-dd'
        )
      }
      totalEmission +=
        (endTimeInSeconds - currentTimeInSeconds) *
        ((co2EmissionData[lastDateKey] && co2EmissionData[lastDateKey].co2s) ||
          0)
    }
    return totalEmission
  }

  /**
   * 可视范围同时显示所有轨迹
   * @params trackId	string	轨迹ID，可为空，为空则适应所有轨迹
   */
  _viewAllTrackInMap(trackId: string, options?: undefined) {
    const latlngs: LatLngExpression[] = []
    let item = {}
    CanvasShipUtils.isEmpty(trackId)
      ? (item = this.items)
      : (item[trackId] = this.items[trackId])
    for (const key in item)
      if (item[key] && item[key].symbol) {
        const data = item[key].data
        if (data && data.length) {
          let lng = data[0].lng
          latlngs.push([data[0].lat, lng])
          lng = data[data.length - 1].lng
          latlngs.push([data[data.length - 1].lat, lng])
          for (let i = 1; i < data.length - 2; i++) {
            latlngs.push([data[i].lat, data[i].lng])
          }
        }
      }
    if (latlngs.length > 0) {
      this._map.fitBounds(
        CanvasShipUtils.getLatLngBoundsBoxArea(latlngs, this._map),
        options
      )
    }
  }
  /**
   * 隐藏指定轨迹
   * @params trackId	String	轨迹ID
   */
  _hide(trackId: string) {
    if (this.has(trackId)) {
      const item = this.items[trackId].symbol
      item && (item.hide(), (this.items[trackId].symbol = null))
    }
    this.trackPlayService.removeTracks([trackId])
  }
  /**
   * 隐藏指定轨迹
   * @params trackId	String	轨迹ID
   */
  hide(trackId: string) {
    this._hide(trackId)
    this.refreshView()
  }
  /**
   * 隐藏全部轨迹
   */
  hideAll() {
    for (const item in this.items) this.hide(item)
    this.refreshView()
  }
  _remove(trackId: string) {
    this.hide(trackId)
    delete this.items[trackId]
  }
  /**
   * 删除指定轨迹
   * @params  trackId	String	轨迹ID
   */
  remove(trackId: any) {
    this._remove(trackId)
    this.refreshView()
  }
  /**
   * 删除所有轨迹
   */
  removeAll() {
    for (const item in this.items) this._remove(item)
  }
  refreshView() {
    this._map.refreshView && this._map.refreshView()
    this._map.shipsService &&
      (!this.hasTrackIsShow()
        ? this._map.shipsService.restoreZIndex()
        : this._map.shipsService.setZIndex(90))
  }
  /**
   * 获取轨迹对象集合
   */
  getItems() {
    return this.items
  }
  /**
   * 获取轨迹数量
   */
  getLength() {
    return Object.keys(this.items).length
  }
  /**
   * 获取所有轨迹集合
   */
  getValues() {
    const tracks = []
    for (const key in this.items) tracks.push(this.items[key])
    return tracks
  }
  /**
   * 获取所有轨迹ID集合
   */
  getKeys() {
    return Object.keys(this.items)
  }
  /**
   * 查询轨迹数据
   * @params mmsi	string	mmsi
   * @params btime	number	起始时间戳，秒
   * @params etime	number	结束时间戳，秒
   * @params going	boolean	是否为连续查询，默认：false
   * @params tp	number	轨迹数据类型，0：普通,1：抽稀,2：完整，默认：0
   * @params vy	number	航程，0：无,1：有，默认：0
   * mmsi, btime, etime, going, tp, vy
   */
  getTrackInfoByUrl() {
    const tracks: any[] = result
    // if (CommUtils.isEmpty(mmsi) || CommUtils.isEmpty(btime) || CommUtils.isEmpty(etime) || btime > etime)
    //   return [];
    // CommUtils.isEmpty(going) && (going = false)
    // CommUtils.isEmpty(tp) && (tp = 0)
    // CommUtils.isEmpty(vy) && (vy = 0)
    // let url = ShipxyOptions.shipDataServer.getShipTrackUrl;
    // url = url.replace('{1}', mmsi).replace('{2}', btime).replace('{3}', etime).replace('{4}', tp).replace('{5}', vy),
    //   this._map.options.ak && (url = url.replace('{k}', this._map.options.ak)),
    //   jQuery.ajaxSettings.async = false,
    //   jQuery.get(url, function (resData) {
    //     let parseData = L.ParseUtils.parseTrackAPI(resData, tp, vy);
    //     if (parseData && 0 == parseData.status && parseData.data) {
    //       tracks = tracks.concat(parseData.data)
    //       if (parseData.continue) {
    //         let utc = parseData.data[parseData.data.length - 1].utc
    //         let currentSegment = this.getTrackInfoByUrl(mmsi, utc, etime, true, tp, vy);
    //         tracks = tracks.concat(currentSegment);
    //       }
    //     }

    //   })
    return tracks
  }
  /**
   * 是否包含指定轨迹
   * @params trackId	String	轨迹ID
   */
  has(trackId: string) {
    return trackId in this.items
  }
  /**
   * 是否有轨迹显示
   */
  hasTrackIsShow() {
    let IsShow = false
    for (const key in this.items)
      if (this.items[key].symbol) {
        IsShow = true
        break
      }
    return IsShow
  }
  /**
   * 获取指定轨迹
   * @params trackId	String	轨迹ID
   */
  get(trackId: string) {
    return this.has(trackId) ? this.items[trackId] : undefined
  }
  setZIndex(zIndex = 160) {
    this._map.canvas_track && this._map.canvas_track._container
      ? (this._map.canvas_track._container.style.zIndex = zIndex)
      : console.error('canvas_track is null')
  }
}

// 确保引入或定义任何在原始JavaScript代码中使用的外部API或类型
